/**
 * EPIC283-B Now readiness evaluator.
 * Consumes canonical fields only — never invents update_mode, next_trigger,
 * source_age, or stage_latency from loosely related timestamps.
 */

import { displayStateContent } from './contentContract';

export type UpdateMode = 'incremental' | 'scan_only';

export type NowReadinessStatus = 'ready' | 'blocked' | 'unavailable';

export type OptionalDiagnostic = {
  value: string | number | null | undefined;
  /** Present only when the server supplied a value. */
  available: boolean;
  display: string;
};

export type NowReadinessInput = {
  /** Required — EPIC282-owned display state from K1 Now window. */
  displayState?: string | null;
  /** Window display_as_of when present. */
  displayAsOf?: string | null;
  /** Window observed_through when present. */
  observedThrough?: string | null;
  /** Top-level query_as_of fallback. */
  queryAsOf?: string | null;
  /** Required — must be server-declared; never inferred. */
  updateMode?: UpdateMode | string | null;
  /**
   * Scan-only next trigger. Null is allowed only with nextTriggerNullReason.
   * Absent (undefined) means the field was not provided.
   */
  nextTrigger?: string | null;
  /** Explicit server reason when nextTrigger is null. */
  nextTriggerNullReason?: string | null;
  /** Optional — show Unavailable if not provided; never calculate. */
  sourceAge?: string | number | null;
  /** Optional — show Unavailable if not provided; never calculate. */
  stageLatency?: string | number | null;
};

export type NowReadinessResult = {
  status: NowReadinessStatus;
  missingRequired: string[];
  displayState: string | null;
  asOf: string | null;
  updateMode: string | null;
  nextTriggerDisplay: string;
  sourceAge: OptionalDiagnostic;
  stageLatency: OptionalDiagnostic;
  /** True only when status === 'ready'. */
  mayClaimTrustedNow: boolean;
  customerLabel: string;
  customerMessage: string;
  blockedReasons: string[];
};

const OPTIONAL_UNAVAILABLE = 'Unavailable';

function optionalDiagnostic(value: string | number | null | undefined): OptionalDiagnostic {
  if (value === undefined || value === null || value === '') {
    return { value: null, available: false, display: OPTIONAL_UNAVAILABLE };
  }
  return { value, available: true, display: String(value) };
}

function resolveAsOf(input: NowReadinessInput): string | null {
  for (const candidate of [input.displayAsOf, input.observedThrough, input.queryAsOf]) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  return null;
}

function normalizeUpdateMode(raw: string | null | undefined): UpdateMode | null {
  if (!raw) return null;
  const v = raw.trim().toLowerCase();
  if (v === 'incremental') return 'incremental';
  if (v === 'scan_only' || v === 'scan-only' || v === 'scan_completion') return 'scan_only';
  return null;
}

/**
 * Evaluate Now readiness against the frozen B required/optional matrix.
 * Does not derive freshness from camera connectivity or loose timestamps.
 */
export function evaluateNowReadiness(input: NowReadinessInput): NowReadinessResult {
  const missingRequired: string[] = [];
  const blockedReasons: string[] = [];

  const displayState =
    typeof input.displayState === 'string' && input.displayState.trim()
      ? input.displayState.trim()
      : null;
  if (!displayState) {
    missingRequired.push('display_state');
  }

  const asOf = resolveAsOf(input);
  if (!asOf) {
    missingRequired.push('as_of_or_latest_completed_at');
  }

  const updateMode = normalizeUpdateMode(
    input.updateMode == null ? null : String(input.updateMode),
  );
  if (!updateMode) {
    missingRequired.push('update_mode');
    blockedReasons.push(
      'Declared update mode is not available yet for this measurement.',
    );
  }

  let nextTriggerDisplay = OPTIONAL_UNAVAILABLE;
  if (updateMode === 'scan_only') {
    if (input.nextTrigger === undefined) {
      missingRequired.push('next_trigger');
      blockedReasons.push(
        'Scan-only mode requires next_trigger or an explicit null reason from the server.',
      );
      nextTriggerDisplay = OPTIONAL_UNAVAILABLE;
    } else if (input.nextTrigger === null || input.nextTrigger === '') {
      const reason =
        typeof input.nextTriggerNullReason === 'string' && input.nextTriggerNullReason.trim()
          ? input.nextTriggerNullReason.trim()
          : null;
      if (!reason) {
        missingRequired.push('next_trigger_null_reason');
        blockedReasons.push(
          'next_trigger is null without an explicit server reason; showing Not scheduled is not allowed without reason.',
        );
        nextTriggerDisplay = OPTIONAL_UNAVAILABLE;
      } else if (/not scheduled/i.test(reason)) {
        nextTriggerDisplay = 'Not scheduled';
      } else {
        nextTriggerDisplay = `Unavailable (${reason})`;
      }
    } else {
      nextTriggerDisplay = String(input.nextTrigger);
    }
  } else if (updateMode === 'incremental') {
    nextTriggerDisplay = 'N/A';
  }

  const sourceAge = optionalDiagnostic(input.sourceAge);
  const stageLatency = optionalDiagnostic(input.stageLatency);

  let status: NowReadinessStatus = 'ready';
  if (missingRequired.length > 0) {
    status = 'blocked';
  }

  const mayClaimTrustedNow = status === 'ready';
  const readinessCopy = displayStateContent('readiness_unavailable');

  return {
    status,
    missingRequired,
    displayState,
    asOf,
    updateMode,
    nextTriggerDisplay,
    sourceAge,
    stageLatency,
    mayClaimTrustedNow,
    customerLabel: mayClaimTrustedNow
      ? displayState || 'Current'
      : readinessCopy.label,
    customerMessage: mayClaimTrustedNow
      ? `Trusted Now path available (${updateMode}; as-of ${asOf})`
      : readinessCopy.message,
    blockedReasons,
  };
}


export type ReadinessDiagnosticRow = {
  key: string;
  label: string;
  value: string;
  /** true when this row explains a hard B block */
  blocking?: boolean;
};

/**
 * Customer/ops diagnostics from declared fields only.
 * Never invents update_mode or ages; surfaces Unavailable when absent.
 */
export function formatNowReadinessDiagnostics(result: NowReadinessResult): ReadinessDiagnosticRow[] {
  const rows: ReadinessDiagnosticRow[] = [
    {
      key: 'status',
      label: 'Trusted Now',
      value: result.mayClaimTrustedNow ? 'Ready' : 'Blocked',
      blocking: !result.mayClaimTrustedNow,
    },
    {
      key: 'display_state',
      label: 'K1 display_state',
      value: result.displayState || 'Unavailable',
      blocking: !result.displayState,
    },
    {
      key: 'as_of',
      label: 'As-of',
      value: result.asOf || 'Unavailable',
      blocking: !result.asOf,
    },
    {
      key: 'update_mode',
      label: 'Declared update_mode',
      value: result.updateMode || 'Unavailable (not declared on K1)',
      blocking: !result.updateMode,
    },
    {
      key: 'next_trigger',
      label: 'Next trigger',
      value: result.nextTriggerDisplay,
    },
    {
      key: 'source_age',
      label: 'Source age',
      value: result.sourceAge.display,
    },
    {
      key: 'stage_latency',
      label: 'Stage latency',
      value: result.stageLatency.display,
    },
  ];
  for (const reason of result.blockedReasons) {
    rows.push({
      key: `blocked:${reason.slice(0, 40)}`,
      label: 'Blocker',
      value: reason,
      blocking: true,
    });
  }
  if (result.missingRequired.length) {
    rows.push({
      key: 'missing',
      label: 'Missing required',
      value: result.missingRequired.join(', '),
      blocking: true,
    });
  }
  return rows;
}

/**
 * Build evaluator input from K1-mapped Now window fields plus optional
 * readiness envelope fields when the server starts emitting them.
 */
export function readinessInputFromK1Now(params: {
  displayState?: string | null;
  displayAsOf?: string | null;
  observedThrough?: string | null;
  queryAsOf?: string | null;
  /** Passthrough only — must come from server; do not invent. */
  updateMode?: string | null;
  nextTrigger?: string | null;
  nextTriggerNullReason?: string | null;
  sourceAge?: string | number | null;
  stageLatency?: string | number | null;
}): NowReadinessInput {
  return {
    displayState: params.displayState,
    displayAsOf: params.displayAsOf,
    observedThrough: params.observedThrough,
    queryAsOf: params.queryAsOf,
    updateMode: params.updateMode,
    nextTrigger: params.nextTrigger,
    nextTriggerNullReason: params.nextTriggerNullReason,
    sourceAge: params.sourceAge,
    stageLatency: params.stageLatency,
  };
}
